import moment from 'moment/min/moment-with-locales';

const humanReadableDateTimeFormat = 'YYYY-MM-DD HH:mm';
const dateTimeSQLFormat = 'YYYY-MM-DD"T"HH24:MI:SS.MS';
const dateTimeFormat = 'YYYY-MM-DDTHH:mm:ss.SSS' as const;
const dateFormat = 'YYYY-MM-DD' as const;

function setLocale(language: string): void {
  moment.locale(language.slice(0, 2));
}

function getLocale(): string {
  return moment.locale();
}

function simpleFormatStr(date: moment.MomentInput, pattern: string = dateTimeFormat): string {
  return moment(date).format(pattern);
}

function formatStr(date: moment.MomentInput, pattern: string = dateTimeFormat): string {
  const str = simpleFormatStr(date, pattern);
  return str.substring(0, 26) + '' + str.substring(27, str.length);
}

function formatLocaleStr(date: moment.MomentInput): string {
  return moment(date).format('L') /* .slice(0, 10) */;
}

function formatLongLocaleStr(date: moment.MomentInput): string {
  return moment(date).format('L LT').slice(0, 16);
}

function toDate(value: moment.MomentInput, fromFormat?: string): Date {
  return moment(value, fromFormat).toDate();
}

function valueOf(value: moment.MomentInput): number {
  return moment(value).valueOf();
}

function isDate(value: unknown): value is Date {
  return value instanceof Date;
}

function toDateSpecific(value: moment.MomentInput, needTime = false): Date | undefined {
  if (!value) {
    return;
  }

  const tmpValue = DateUtils.toDate(value);
  if (!DateUtils.isDate(tmpValue)) {
    return undefined;
  }

  if (needTime === false) {
    tmpValue.setHours(0);
    tmpValue.setMinutes(0);
  }
  tmpValue.setSeconds(0);
  tmpValue.setMilliseconds(0);

  return tmpValue;
}

function isSame(
  date1: moment.MomentInput,
  date2: moment.MomentInput,
  granularity?: moment.unitOfTime.StartOf
): boolean {
  return moment(date1).isSame(date2, granularity);
}

function compareDateValue(value1: moment.MomentInput, value2: moment.MomentInput): boolean {
  const value1Date = toDateSpecific(value1);
  const value2Date = toDateSpecific(value2);

  if (value2Date && value1Date) {
    return new Date(value1Date).getTime() !== new Date(value2Date).getTime();
  }

  return !!value1Date || !!value2Date;
}

function getMoment(date: moment.MomentInput, format?: string): moment.Moment {
  let m: moment.Moment;
  if (format) {
    m = moment(date);
  } else {
    m = moment(date, format);
  }

  return m;
}

function add(
  date: moment.MomentInput,
  toAdd: moment.DurationInputArg1,
  unit: moment.DurationInputArg2,
  format?: string
): Date {
  return getMoment(date, format).add(toAdd, unit).toDate();
}

function subtract(
  date: moment.MomentInput,
  toSubtract: moment.DurationInputArg1,
  unit: moment.DurationInputArg2,
  format?: string
): Date {
  return getMoment(date, format).subtract(toSubtract, unit).toDate();
}

function diff(date1: moment.MomentInput, date2: moment.MomentInput, unit?: moment.DurationInputArg2): number {
  return moment(date1).diff(moment(date2), unit);
}

function toRelative(date: moment.MomentInput): string {
  return getMoment(date).fromNow();
}

enum Month {
  JANUARY,
  FEBRUARY,
  MARCH,
  APRIL,
  MAY,
  JUNE,
  JULY,
  AUGUST,
  SEPTEMBER,
  OCTOBER,
  NOVEMBER,
  DECEMBER,
}

const DateUtils = {
  dateTimeSQLFormat,
  dateTimeFormat,
  dateFormat,
  humanReadableDateTimeFormat,
  setLocale,
  getLocale,
  simpleFormatStr,
  formatStr,
  formatLocaleStr,
  formatLongLocaleStr,
  toDate,
  valueOf,
  isDate,
  toDateSpecific,
  toRelative,
  isSame,
  compareDateValue,
  add,
  subtract,
  diff,
};

export { DateUtils, Month };
